import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, IonModal, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { GepjarmuService } from '../gepjarmu.service';
import { NavNyomtatvanyService } from '../../nav-nyomtatvany/nav-nyomtatvany.service';
import { naploNyomtat, NyomtatasOszlop } from '../../naplo/naplo-nyomtatas';

const HONAPOK = ['január', 'február', 'március', 'április', 'május', 'június', 'július', 'augusztus', 'szeptember', 'október', 'november', 'december'];
const honapNev = (h: number) => { const n = HONAPOK[(Number(h) || 1) - 1] || ''; return n.charAt(0).toUpperCase() + n.slice(1); };
const ROMAI = ['', 'I.', 'II.', 'III.', 'IV.'];
const CSOPORT_NEV: Record<string, string> = { o04: '„0”–„4”', o610: '„6”–„10”', o5: '„5”; „14–15”' };
const TULAJDONVISZONY: Record<string, string> = {
  sajat: 'Saját tulajdon', penzugyi_lizing: 'Pénzügyi lízing', zart_vegu_lizing: 'Zárt végű lízing', nyilt_vegu_lizing: 'Nyílt végű (operatív) lízing',
  berelt: 'Bérelt', maganszemely: 'Magánszemély tulajdonában – költségelszámolással',
};
const HONAP_SZABALY = [
  { v: 'kovetkezo_ho', c: 'A szerzést / első költségelszámolást KÖVETŐ hónaptól a megszűnés hónapjáig (Gjt. 17/C. §)' },
  { v: 'barmely_nap', c: 'A használat bármely napja a teljes havi adót viszi (egyszerűsített, a törvénynél szigorúbb)' },
];
type Ful = 'szamfejtes' | 'lista' | 'adotetelek' | 'bevallas' | 'konyveles';

/*  GÉPJÁRMŰ – CÉGAUTÓADÓ (Gjt. 17/A–17/G. §; backend: gepjarmu-cegautoado.js, /api/gepjarmu-ado).
 *  A Bér korábbi cégautós képernyőinek (ber/cegauto: számfejtés, havi lista, XX01-export) utódja, öt fülön:
 *   - Számfejtés: a negyedév (vagy egy hónap) adója járművenként – a járműtörzsből számolva és TÁROLVA;
 *     egy tétel indoklással kézzel felülírható, újraszámoláskor a kézi érték megmarad;
 *   - Lista: havi / negyedéves / éves lista járművenként, nyomtatható;
 *   - Adótételek: az év tételei (kW-sáv × osztálycsoport) az intézményre, „ellenőrizendő” jelzéssel;
 *   - Bevallás: a negyedév 2601-es bevallása a NAV-nyomtatványkitöltőben (előtöltve), a 02-es lap képe,
 *     CSV / Excel munkalap;
 *   - Könyvelés: a negyedév fizetendő adója a Könyvelésbe (kettős: vegyes bizonylat), visszavonható.
 *  A járművek a Járművek képernyőn vehetők fel (a cégautóadó-mezők a jármű „Cégautóadó” részén). */
@Component({
  selector: 'app-gj-cegautoado',
  templateUrl: './cegautoado.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../gepjarmu-shared.scss', './cegautoado.component.scss'],
})
export class GjCegautoadoComponent implements OnInit, OnDestroy {
  @ViewChild('keziModal') keziModal: IonModal;
  readonly fulek: { k: Ful; c: string; ikon: string }[] = [
    { k: 'szamfejtes', c: 'Számfejtés', ikon: 'calculator-outline' },
    { k: 'lista', c: 'Lista', ikon: 'list-outline' },
    { k: 'adotetelek', c: 'Adótételek', ikon: 'options-outline' },
    { k: 'bevallas', c: 'Bevallás (2601)', ikon: 'paper-plane-outline' },
    { k: 'konyveles', c: 'Könyvelés', ikon: 'git-network-outline' },
  ];
  ful: Ful = 'szamfejtes';
  readonly honapok = HONAPOK.map((_, i) => ({ v: i + 1, c: honapNev(i + 1) }));
  readonly negyedevek = [1, 2, 3, 4];
  readonly romai = ROMAI;
  readonly csoportNev = CSOPORT_NEV;
  readonly honapSzabalyok = HONAP_SZABALY;
  readonly savok = [1, 2, 3, 4];
  readonly csoportok = ['o04', 'o610', 'o5'];
  honapNev = honapNev;
  tulajdonviszonyNev = (v: string) => TULAJDONVISZONY[v] || v || '—';

  /** A számfejtés és a lista időszaka: negyedév (alap) vagy hónap; a listában az egész év is. */
  nezet: 'negyedev' | 'ho' = 'negyedev';
  listaNezet: 'ho' | 'negyedev' | 'ev' = 'negyedev';
  negyedev = Math.ceil((new Date().getMonth() + 1) / 3);
  ho = new Date().getMonth() + 1;

  adat: any = null;          // számfejtés { sorok, osszesen, honapok }
  lista: any = null;
  param: any = null;
  lap: any = null;           // 2601-02
  bevallasok: any[] = [];
  feladas: any = null;
  fut = false;
  csakAdokoteles = false;
  paramMentes = false;
  kezi: any = null;
  mod: 'E' | 'M' = 'E';
  szamlak = { raforditas: '', kotelezettseg: '' };
  megjegyez = false;
  hiba = '';

  private sub?: Subscription;
  private elozo = '';

  constructor(public gj: GepjarmuService, private ny: NavNyomtatvanyService, private route: ActivatedRoute, private router: Router,
              private toast: ToastController, private alert: AlertController) {}

  get aktiv() { return this.gj.aktiv; }
  get ev(): number { return this.aktiv.ev; }

  async ngOnInit() {
    await this.gj.init();
    const f = this.route.snapshot.queryParamMap.get('ful') as Ful | null;
    if (f && this.fulek.some((x) => x.k === f)) this.ful = f;
    const n = Number(this.route.snapshot.queryParamMap.get('negyedev'));
    if (n >= 1 && n <= 4) this.negyedev = n;
    //  A cég vagy az év (a fejlécben) változásakor újratöltünk.
    this.sub = this.gj.aktiv$.subscribe((a) => {
      const k = `${a.ugyfelId}|${a.ev}|${a.intezmeny}`;
      if (k === this.elozo) return;
      this.elozo = k;
      this.betolt();
    });
  }
  ngOnDestroy() { this.sub?.unsubscribe(); }

  fulValt(f: Ful) {
    this.ful = f;
    this.router.navigate([], { relativeTo: this.route, queryParams: { ful: f }, queryParamsHandling: 'merge', replaceUrl: true });
    this.betolt();
  }

  /** Az aktív fül adatai (a többi fül csak megnyitáskor töltődik). */
  async betolt() {
    this.hiba = '';
    if (!this.aktiv.ugyfelId) { this.adat = this.lista = this.lap = this.feladas = null; this.bevallasok = []; return; }
    if (!this.param || this.param.ev !== this.ev) await this.paramBetolt();
    try {
      if (this.ful === 'szamfejtes') await this.szamfejtesBetolt();
      else if (this.ful === 'lista') await this.listaBetolt();
      else if (this.ful === 'bevallas') await this.bevallasBetolt();
      else if (this.ful === 'konyveles') await this.feladasBetolt();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült betölteni az adatokat.'; }
  }

  /* ══════════ SZÁMFEJTÉS ══════════ */
  private idoszakQ(): string { return `?ev=${this.ev}` + (this.nezet === 'ho' ? `&ho=${this.ho}` : `&negyedev=${this.negyedev}`); }
  get idoszakNev(): string {
    return this.nezet === 'ho' ? `${this.ev}. ${HONAPOK[this.ho - 1]}` : `${this.ev}. ${ROMAI[this.negyedev]} negyedév`;
  }
  async nezetValt(n: 'negyedev' | 'ho') { this.nezet = n; if (n === 'ho' && Math.ceil(this.ho / 3) !== this.negyedev) this.ho = (this.negyedev - 1) * 3 + 1; await this.szamfejtesBetolt(); }
  async negyedevValt(n: number) { this.negyedev = Number(n); this.ho = (this.negyedev - 1) * 3 + 1; await this.betolt(); }
  async hoValt(h: number) { this.ho = Number(h); this.negyedev = Math.ceil(this.ho / 3); await this.szamfejtesBetolt(); }

  async szamfejtesBetolt() {
    this.adat = null;
    try { this.adat = await this.gj.get('ado:/szamfejtes/' + this.aktiv.ugyfelId + this.idoszakQ()); }
    catch (e: any) { this.hibaJelez(e, 'Nem sikerült betölteni a számfejtést.'); }
  }
  get sorok(): any[] { return (this.adat?.sorok || []).filter((s: any) => !this.csakAdokoteles || s.adokoteles || s.kezi); }
  get vanElavult(): boolean { return (this.adat?.sorok || []).some((s: any) => s.elavult); }
  get autokSzama(): number { return new Set((this.adat?.sorok || []).filter((s: any) => s.ado > 0).map((s: any) => s.jarmu_id)).size; }
  get szamfejtetlen(): number[] {
    const van = new Set((this.adat?.sorok || []).map((s: any) => s.ho));
    return (this.adat?.honapok || []).filter((h: number) => !van.has(h));
  }
  get vanFeladott(): boolean { return (this.adat?.sorok || []).some((s: any) => s.naplo_tetel_id); }

  async szamol() {
    if (!this.aktiv.ugyfelId) return;
    this.fut = true;
    try {
      const body: any = { ev: this.ev };
      if (this.nezet === 'ho') body.ho = this.ho; else body.negyedev = this.negyedev;
      const r: any = await this.gj.post('ado:/szamfejtes/' + this.aktiv.ugyfelId + '/szamol', body);
      this.adat = { ...(this.adat || {}), ...r };
      const kezi = (r.honapok_eredmeny || []).reduce((a: number, h: any) => a + (h.kezi_megtartva || 0), 0);
      await this.paramBetolt();
      this.ok(`Számfejtve: ${this.idoszakNev}.` + (kezi ? ` ${kezi} kézi tétel megmaradt.` : '') + (this.vanFeladott ? ' A Könyvelésbe feladott összeget ellenőrizd a Könyvelés fülön.' : ''));
    } catch (e: any) { this.hibaJelez(e, 'A számfejtés nem sikerült.'); }
    finally { this.fut = false; }
  }

  async torol() {
    const al = await this.alert.create({
      header: 'Számfejtés törlése',
      message: `Törli a(z) ${this.idoszakNev} cégautóadó-számfejtését? A kézzel felülírt tételek megmaradnak.`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive', handler: async () => {
        try { await this.gj.del('ado:/szamfejtes/' + this.aktiv.ugyfelId + this.idoszakQ()); await this.szamfejtesBetolt(); this.ok('Törölve.'); }
        catch (e: any) { this.hibaJelez(e, 'Nem sikerült.'); }
      } }],
    });
    await al.present();
  }

  keziNyit(s: any) {
    this.kezi = { s, ado: s.kezi ? s.kezi_ado : s.ado, levonas: s.kezi ? s.kezi_levonas : null, indok: s.kezi_indok || '' };
    this.keziModal.present();
  }
  async keziMent() {
    try {
      await this.gj.put('ado:/szamfejtes/tetel/' + this.kezi.s.id, { ado: this.kezi.ado, levonas: this.kezi.levonas, indok: this.kezi.indok });
      await this.keziModal.dismiss(); await this.szamfejtesBetolt(); this.ok('Kézi érték mentve.');
    } catch (e: any) { this.hibaJelez(e, 'Mentési hiba.'); }
  }
  async keziTorol() {
    try {
      await this.gj.del('ado:/szamfejtes/tetel/' + this.kezi.s.id + '/kezi');
      await this.keziModal.dismiss(); await this.szamfejtesBetolt(); this.ok('A számított érték visszaállt.');
    } catch (e: any) { this.hibaJelez(e, 'Nem sikerült.'); }
  }
  jarmuvekre() { this.router.navigateByUrl('/gepjarmu/jarmuvek'); }

  /* ══════════ LISTA ══════════ */
  async listaNezetValt(n: 'ho' | 'negyedev' | 'ev') { this.listaNezet = n; await this.listaBetolt(); }
  get listaIdoszakNev(): string {
    if (this.listaNezet === 'ev') return `${this.ev}. év`;
    return this.listaNezet === 'ho' ? `${this.ev}. ${HONAPOK[this.ho - 1]}` : `${this.ev}. ${ROMAI[this.negyedev]} negyedév`;
  }
  async listaBetolt() {
    this.lista = null;
    const q = `?ev=${this.ev}` + (this.listaNezet === 'ho' ? `&ho=${this.ho}` : this.listaNezet === 'negyedev' ? `&negyedev=${this.negyedev}` : '');
    try { this.lista = await this.gj.get('ado:/lista/' + this.aktiv.ugyfelId + q); }
    catch (e: any) { this.hibaJelez(e, 'Nem sikerült betölteni a listát.'); }
  }
  get listaElavult(): boolean { return (this.lista?.sorok || []).some((s: any) => s.elavult); }
  hoAdo(a: any, ho: number): number | null { const h = a.honapok?.[ho]; return h ? h.ado : null; }

  nyomtat() {
    const l = this.lista;
    if (!l) return;
    const ft = (v: any) => (v == null || v === '' ? '' : Number(v).toLocaleString('hu-HU'));
    let oszlopok: NyomtatasOszlop[];
    let sorok: any[];
    let ertek: (s: any, o: NyomtatasOszlop) => string;
    if (this.listaNezet === 'ho') {
      oszlopok = [
        { k: 'rendszam', c: 'Rendszám' }, { k: 'jarmu', c: 'Gyártmány, típus', szeles: true }, { k: 'kw', c: 'kW', num: true },
        { k: 'osztaly', c: 'Osztály' }, { k: 'hanyad', c: 'Hányad %', num: true }, { k: 'tarifa', c: 'Tarifa', num: true },
        { k: 'ado', c: 'Adó', num: true }, { k: 'levonas', c: 'Levonás', num: true }, { k: 'fizetendo', c: 'Fizetendő', num: true },
        { k: 'megj', c: 'Megjegyzés', szeles: true },
      ];
      sorok = l.sorok;
      ertek = (s, o) => ({
        rendszam: s.rendszam, jarmu: [s.gyartmany, s.tipus].filter(Boolean).join(' '), kw: String(s.teljesitmeny_kw), osztaly: s.korny_osztaly,
        hanyad: s.tulajdoni_hanyad < 100 ? String(s.tulajdoni_hanyad) : '', tarifa: s.adokoteles ? ft(s.tarifa) : '',
        ado: ft(s.ado), levonas: s.levonas ? ft(s.levonas) : '', fizetendo: ft(s.fizetendo),
        megj: [s.kezi ? `kézi: ${s.kezi_indok || ''}` : '', s.indok || ''].filter(Boolean).join(' · '),
      } as any)[o.k] ?? '';
    } else {
      const h = l.honapok as number[];
      oszlopok = [
        { k: 'rendszam', c: 'Rendszám' }, { k: 'jarmu', c: 'Gyártmány, típus', szeles: true }, { k: 'kw', c: 'kW', num: true },
        { k: 'osztaly', c: 'Osztály' },
        ...h.map((ho) => ({ k: 'h' + ho, c: this.listaNezet === 'ev' ? String(ho) + '.' : honapNev(ho), num: true })),
        { k: 'ado', c: 'Adó összesen', num: true }, { k: 'levonas', c: 'Levonás', num: true }, { k: 'fizetendo', c: 'Fizetendő', num: true },
      ];
      sorok = l.autok;
      ertek = (a, o) => {
        if (o.k.startsWith('h')) { const v = this.hoAdo(a, Number(o.k.slice(1))); return v == null ? '' : ft(v); }
        return ({ rendszam: a.rendszam, jarmu: [a.gyartmany, a.tipus].filter(Boolean).join(' '), kw: String(a.teljesitmeny_kw),
          osztaly: a.korny_osztaly, ado: ft(a.ado), levonas: a.levonas ? ft(a.levonas) : '', fizetendo: ft(a.fizetendo) } as any)[o.k] ?? '';
      };
    }
    const ossz = (o: NyomtatasOszlop) => {
      if (o.k.startsWith('h') && o.num) return ft(l.honap_osszesen?.[Number(o.k.slice(1))]?.ado ?? 0);
      if (['ado', 'levonas', 'fizetendo'].includes(o.k)) return ft(l.osszesen[o.k]);
      return '';
    };
    const szurok: [string, string][] = [['Időszak', this.listaIdoszakNev]];
    if (l.nincs_szamfejtve?.length) szurok.push(['Számfejtetlen hónap', l.nincs_szamfejtve.map((x: number) => honapNev(x)).join(', ')]);
    naploNyomtat({
      cim: this.listaNezet === 'ho' ? 'Cégautóadó – havi lista' : this.listaNezet === 'ev' ? 'Cégautóadó – éves lista' : 'Cégautóadó – negyedéves lista',
      ugyfel: { nev: l.ugyfel?.nev || this.aktiv.ugyfelNev, adoszam: l.ugyfel?.adoszam, cim: l.ugyfel?.cim },
      ev: this.ev, mod: 'Gépjármű · cégautóadó (Gjt. 17/A–17/G. §)', szurok, oszlopok, sorok, ertek,
      osszegSor: (o) => ossz(o), uresOszlopNelkul: false,
    });
  }

  /* ══════════ ADÓTÉTELEK ══════════ */
  async paramBetolt() {
    if (!this.aktiv.intezmeny) return;
    try { this.param = await this.gj.get('ado:/param/' + encodeURIComponent(this.aktiv.intezmeny) + '?ev=' + this.ev); }
    catch { this.param = null; }
  }
  async paramMent() {
    this.paramMentes = true;
    try {
      await this.gj.put('ado:/param/' + encodeURIComponent(this.aktiv.intezmeny) + '?ev=' + this.ev, this.param);
      await this.paramBetolt();
      this.ok('Adótételek mentve. A már számfejtett hónapokat számold újra.');
    } catch (e: any) { this.hibaJelez(e, 'Mentési hiba.'); }
    finally { this.paramMentes = false; }
  }

  /* ══════════ BEVALLÁS (2601) ══════════ */
  get kod(): string { return `${String(this.ev).slice(2)}01`; }
  get hatarido(): string {
    const ho = this.negyedev * 3 + 1;
    return ho > 12 ? `${this.ev + 1}. január 20.` : `${this.ev}. ${HONAPOK[ho - 1]} 20.`;
  }
  sorokHonap(h: number): any[] { return (this.lap?.sorok_04_15 || []).filter((r: any) => r.honap === h); }
  async bevallasBetolt() {
    this.lap = null;
    const [lap, lista] = await Promise.all([
      this.gj.get(`ado:/export/${this.aktiv.ugyfelId}?ev=${this.ev}&negyedev=${this.negyedev}`),
      this.ny.get<any>(`/bevallasok/GEPJARMU/${this.aktiv.ugyfelId}?ev=${this.ev}`).catch(() => ({ sorok: [] })),
    ]);
    this.lap = lap;
    //  Alapból az adószám dönt: adószámmal „nem magánszemélyként”, anélkül magánszemélyként.
    this.mod = String(this.lap?.ugyfel?.adoszam || '').replace(/\D/g, '').length === 11 ? 'E' : 'M';
    //  A cég 2601-esei: a Gépjárműben készültek és a korábban a Bérben (vagy a Könyvelésben) indítottak is.
    this.bevallasok = ((lista as any)?.sorok || []).filter((b: any) => /^\d{2}01$/.test(String(b.kod)) && (b.gyakorisag === 'negyedeves' || (!b.ho && b.negyedev)));
  }
  negyedevBevallasai(n: number): any[] { return this.bevallasok.filter((b) => Number(b.ev) === this.ev && Number(b.negyedev) === n); }
  modulCim(m: string) { return m === 'BER' ? 'Bér' : m === 'NAPLO' ? 'Könyvelés' : 'Gépjármű'; }
  readonly allapotNev: Record<string, string> = {
    tervezet: 'tervezet', hibas: 'hibás', ellenorzott: 'ellenőrzött', bekuldve: 'beküldve', befogadva: 'befogadva', elutasitva: 'elutasítva',
  };
  /*  A bevallás a Gépjármű keretében nyílik (a kitöltő a bevallás saját moduljából dolgozik) – a Bérben
   *  korábban készült 2601 is, így innen nem kell modult váltani. */
  bevallasNyit(b: any) { this.router.navigate(['/gepjarmu/bevallas', b.id]); }
  async bevallasUj(n: number) {
    this.fut = true;
    try {
      const katalogus: any = await this.ny.get('/katalogus');
      if (!(katalogus.lista || []).some((k: any) => k.kod === this.kod && k.letoltve)) await this.ny.post(`/sablon/${this.kod}/letolt`, {});
      const b: any = await this.ny.post('/bevallas', { modul: 'GEPJARMU', ugyfel_id: this.aktiv.ugyfelId, kod: this.kod, ev: this.ev,
        gyakorisag: 'negyedeves', negyedev: n, kitoltesParam: { mod: this.mod } });
      this.router.navigate(['/gepjarmu/bevallas', b.id]);
    } catch (e: any) { this.hibaJelez(e, `Nem sikerült létrehozni a(z) ${this.kod}-es bevallást.`); }
    finally { this.fut = false; }
  }
  /** A negyedév bevallása: a legutóbbi nem elutasított megnyitása, ha nincs, új (előtöltve). */
  async bevallasNyitVagyUj() {
    const van = this.negyedevBevallasai(this.negyedev).find((b) => b.allapot !== 'elutasitva' && b.forras !== 'kulso');
    if (van) this.bevallasNyit(van); else await this.bevallasUj(this.negyedev);
  }
  async munkalap(tipus: 'csv' | 'xlsx') {
    try { await this.gj.getFajl(`ado:/export/${this.aktiv.ugyfelId}/${tipus}?ev=${this.ev}&negyedev=${this.negyedev}`, `cegautoado_${this.kod}-02_${this.ev}Q${this.negyedev}.${tipus}`); }
    catch (e: any) { this.hibaJelez(e, 'A letöltés nem sikerült.'); }
  }

  /* ══════════ KÖNYVELÉS (feladás) ══════════ */
  async feladasBetolt() {
    this.feladas = null;
    try {
      this.feladas = await this.gj.get(`ado:/feladas/${this.aktiv.ugyfelId}?ev=${this.ev}`);
      this.szamlak = { raforditas: this.feladas.szamlak?.raforditas || '', kotelezettseg: this.feladas.szamlak?.kotelezettseg || '' };
    } catch (e: any) { this.hibaJelez(e, 'Nem sikerült betölteni a feladást.'); }
  }
  get feladhatoGomb(): boolean { return !!this.feladas?.feladhato && !!this.feladas?.konyvelhet && !this.feladas?.ev_lezarva; }
  async felad(n: any) {
    const ujra = !!n.feladva;
    const al = await this.alert.create({
      header: ujra ? 'Újrafeladás a Könyvelésbe' : 'Feladás a Könyvelésbe',
      message: `${this.ev}. ${ROMAI[n.negyedev]} negyedév: ${n.osszeg.toLocaleString('hu-HU')} Ft fizetendő cégautóadó vegyes bizonylatként `
        + `(T ${this.szamlak.raforditas} / K ${this.szamlak.kotelezettseg}, ${n.datum}).` + (ujra ? ' A korábbi bizonylat törlődik, és a mostani összeggel újra készül.' : ''),
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: ujra ? 'Újrafeladás' : 'Feladás', role: 'ok' }],
    });
    await al.present();
    if ((await al.onDidDismiss()).role !== 'ok') return;
    this.fut = true;
    try {
      const r: any = await this.gj.post(`ado:/feladas/${this.aktiv.ugyfelId}`, { ev: this.ev, negyedev: n.negyedev,
        raforditas_szamla: this.szamlak.raforditas, kotelezettseg_szamla: this.szamlak.kotelezettseg, megjegyez: this.megjegyez });
      this.ok(r.ures ? (r.uzenet || 'Nincs mit feladni.') : `Feladva: ${r.bizonylatszam} (${Number(r.osszeg).toLocaleString('hu-HU')} Ft).`);
      await this.feladasBetolt();
    } catch (e: any) { this.hibaJelez(e, 'A feladás nem sikerült.'); }
    finally { this.fut = false; }
  }
  async visszavon(n: any) {
    const al = await this.alert.create({
      header: 'Feladás visszavonása',
      message: `Törli a Könyvelésből a(z) ${this.ev}. ${ROMAI[n.negyedev]} negyedév cégautóadó-bizonylatát (${n.feladva.osszeg.toLocaleString('hu-HU')} Ft)?`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Visszavonás', role: 'destructive' }],
    });
    await al.present();
    if ((await al.onDidDismiss()).role !== 'destructive') return;
    this.fut = true;
    try { await this.gj.del(`ado:/feladas/${n.feladva.id}`); this.ok('A feladás visszavonva.'); await this.feladasBetolt(); }
    catch (e: any) { this.hibaJelez(e, 'Nem sikerült visszavonni.'); }
    finally { this.fut = false; }
  }
  szamlaAlap() { this.szamlak = { ...this.feladas.alap_szamlak }; }

  /* ── üzenetek ── */
  private async hibaJelez(e: any, alap: string) {
    let m = e?.error?.error;
    if (!m && e?.error instanceof Blob) { try { m = JSON.parse(await e.error.text())?.error; } catch { /* nem JSON */ } }
    (await this.toast.create({ message: m || alap, duration: 4000, color: 'danger' })).present();
  }
  private async ok(m: string) { (await this.toast.create({ message: m, duration: 2600, color: 'success' })).present(); }
}
